/**
 * Ethos Virtual - DISPLAY pricing (single source of truth for the marketing UI)
 *
 * Every dollar amount shown on the public pricing surfaces (Pricing.tsx,
 * SmbPlansDropdown.tsx, FAQ.tsx) is read from this file so the numbers exist in
 * exactly one place. It deliberately does NOT replace
 * src/lib/constants/plans.ts, which remains the entitlement/billing model
 * (agent caps, language caps, included minutes, plan gating).
 *
 * PRICING V5 (2026-07-07)
 * -----------------------
 * Company-wide reprice. SMB tiers drop about 30% in both currencies and the
 * USD Business/Enterprise stickers are realigned to a proper FX ratio
 * (roughly 1.43x CAD over USD):
 *   SMB monthly  USD 69 / 139 / 199        CAD 99 / 199 / 299
 *   SMB yearly   USD 749 / 1,499 / 2,099   CAD 1,049 / 2,099 / 3,199
 *   Business     USD 699                   CAD 999 (unchanged)
 *   Enterprise   USD 999                   CAD 1,499 (unchanged)
 *   Two-way add-on (Standard): USD +60 / CAD +100 per month
 * CAD Business/Enterprise links are unchanged; every other link is a v5
 * recreation (payment links are immutable). SMB has NO setup fee anywhere.
 * (v5 left overage rates unchanged; they were then repriced in v6, below.)
 *
 * OVERAGE V6 (2026-07-08)
 * -----------------------
 * SMB overage reprice, locked by Kurt 2026-07-08. The per-additional-minute
 * rate becomes 0.25 (Starter) / 0.24 (Standard) / 0.23 (High-Volume), with the
 * SAME numerals in USD and CAD (replacing USD 0.49/0.47/0.45 and CAD
 * 0.69/0.66/0.63). Business (0.44) and Enterprise (0.35) overage is unchanged.
 * Plan prices are unchanged (pricing v5 stays). The old 0.45/min floor is
 * dead; the new floor is 0.23.
 *
 * TWO-CURRENCY MODEL (2026-07)
 * ----------------------------
 * Ethos is a USD-first brand, but visitors can toggle the whole pricing surface
 * between US$ (USD) and CA$ (CAD). Every plan therefore carries a full pricing
 * block per currency: monthly price, friendly annual total, per-month
 * equivalent, marketing overage rate, setup fee, and the monthly / yearly
 * Stripe checkout links.
 *
 *  - USD is the NATIVE currency: its Stripe Payment Links are the real, live,
 *    hardcoded links (verified). USD is the default currency for every visitor
 *    except those geolocated to Canada (see src/middleware.ts).
 *  - CAD is the CROSS currency: the CAD Stripe links do not exist yet. Their
 *    URLs are read from per-plan / per-interval NEXT_PUBLIC_ env vars. When an
 *    env var is absent (the current state), the CTA GRACEFULLY falls back to
 *    "Contact us for CAD billing" -> /contact, exactly like the yearly-link
 *    fallback. This lets the currency toggle ship before the CAD Stripe links
 *    are created. We NEVER show a USD checkout link under a CAD price (never
 *    charge USD while displaying CAD).
 *
 * When the CAD Stripe links are created, the Stripe webhook must read the
 * currency from the Checkout Session metadata (billing_currency) rather than
 * assuming USD; see the note in the webhook handler.
 *
 * DECISION RESOLVED (Kurt, 2026-07-08): billing follows the tiered marketing
 * rates. plans.ts historically carried a flat 0.45 overageRateUSD for all
 * three SMB tiers while marketing showed tiered rates; as of overage v6 both
 * sides use the same tiered 0.25 / 0.24 / 0.23 (Starter / Standard /
 * High-Volume) rates, so display and billing agree.
 *
 * Setup fees: Small Business plans (Starter / Standard / High-Volume) have NO
 * setup fee. Business and Enterprise keep the one-time $300 setup fee (same
 * numeric value in both currencies, quoted in the shown currency).
 *
 * Yearly billing: pay once per year, 10% off. The friendly annual totals were
 * approved directly (they are close to but not mechanically
 * round(monthly * 12 * 0.9)); the per-month equivalent shown in yearly mode is
 * round(yearlyTotal / 12).
 */

/** SMB self-serve tier keys (mirrors the stripe-ethos webhook contract). */
export type SmbPlanKey = "starter" | "standard" | "high-volume";

/** All plans with published display pricing (Corporate is custom-priced). */
export type DisplayPlanKey = SmbPlanKey | "business" | "enterprise";

export type BillingInterval = "monthly" | "yearly";

/** Supported display / checkout currencies. USD is native, CAD is the cross. */
export type Currency = "USD" | "CAD";

/** The default currency for this brand (Canada visitors default to CAD). */
export const DEFAULT_CURRENCY: Currency = "USD";

/** Yearly billing discount, in percent. */
export const YEARLY_DISCOUNT_PCT = 10;

/** One-time setup fee for Business and Enterprise plans (both currencies). */
export const SETUP_FEE = 300;

export interface PlanCurrencyPricing {
  /** Standard monthly price. */
  price: number;
  /** Friendly annual total, billed once per year (approved directly). */
  yearlyTotal: number;
  /** Per-month equivalent shown in yearly mode: round(yearlyTotal / 12). */
  yearlyPerMonth: number;
  /** Marketing overage rate, per additional minute (see file header). */
  overage: number;
  /** One-time setup fee. 0 means "No setup fee" (an SMB selling point). */
  setupFee: number;
  /**
   * Monthly checkout link. For the NATIVE currency (USD) this is the live,
   * hardcoded Stripe Payment Link. For the CROSS currency (CAD) it is read
   * from an env var and may be undefined, in which case the CTA falls back to
   * the contact page (see contactHref / resolveCheckoutUrl).
   */
  monthlyUrl?: string;
  /** Yearly checkout link. Same native/cross rules as monthlyUrl. */
  yearlyUrl?: string;
}

export type CurrencyPricingMap = Record<DisplayPlanKey, PlanCurrencyPricing>;

/**
 * Cross-currency (CAD) checkout links come from env so the toggle can ship
 * before the CAD Stripe Payment Links exist. Absent -> graceful contact
 * fallback. Names are stable so ops can paste links in later without a code
 * change.
 *
 * Monthly:
 *   NEXT_PUBLIC_STRIPE_SMB_STARTER_CAD_URL
 *   NEXT_PUBLIC_STRIPE_SMB_STANDARD_CAD_URL
 *   NEXT_PUBLIC_STRIPE_SMB_HIGH_VOLUME_CAD_URL
 *   NEXT_PUBLIC_STRIPE_BUSINESS_CAD_URL
 *   NEXT_PUBLIC_STRIPE_ENTERPRISE_CAD_URL
 * Yearly:
 *   NEXT_PUBLIC_STRIPE_SMB_STARTER_CAD_YEARLY_URL
 *   NEXT_PUBLIC_STRIPE_SMB_STANDARD_CAD_YEARLY_URL
 *   NEXT_PUBLIC_STRIPE_SMB_HIGH_VOLUME_CAD_YEARLY_URL
 *   NEXT_PUBLIC_STRIPE_BUSINESS_CAD_YEARLY_URL
 *   NEXT_PUBLIC_STRIPE_ENTERPRISE_CAD_YEARLY_URL
 *
 * These must be inlined at build time (Next.js only exposes literal
 * process.env.NEXT_PUBLIC_* references to the browser), so read each one
 * explicitly rather than by computed key.
 */
// CAD checkout links live in the Ethos Stripe account. SMB links are the v5
// reprice recreations (2026-07-07); Business/Enterprise CAD prices are
// unchanged in v5, so their 2026-07-02 links are kept. Env vars can still
// override if ever set; the hardcoded live links are the default.
const CAD_URLS: Record<DisplayPlanKey, { monthly?: string; yearly?: string }> = {
  starter: {
    monthly: process.env.NEXT_PUBLIC_STRIPE_SMB_STARTER_CAD_URL || "https://buy.stripe.com/bJe5kFbmvan4bEU19jew80A",
    yearly: process.env.NEXT_PUBLIC_STRIPE_SMB_STARTER_CAD_YEARLY_URL || "https://buy.stripe.com/aFabJ3cqzdzgcIYbNXew80B",
  },
  standard: {
    monthly: process.env.NEXT_PUBLIC_STRIPE_SMB_STANDARD_CAD_URL || "https://buy.stripe.com/28E28tgGP66O6kAf09ew80C",
    yearly: process.env.NEXT_PUBLIC_STRIPE_SMB_STANDARD_CAD_YEARLY_URL || "https://buy.stripe.com/14A8wRcqzdzg10g7xHew80D",
  },
  "high-volume": {
    monthly: process.env.NEXT_PUBLIC_STRIPE_SMB_HIGH_VOLUME_CAD_URL || "https://buy.stripe.com/7sY28tcqzan49wM2dnew80E",
    yearly: process.env.NEXT_PUBLIC_STRIPE_SMB_HIGH_VOLUME_CAD_YEARLY_URL || "https://buy.stripe.com/9B66oJ3U33YG24kaJTew80F",
  },
  business: {
    monthly: process.env.NEXT_PUBLIC_STRIPE_BUSINESS_CAD_URL || "https://buy.stripe.com/fZu00lduDgLsfVaf09ew80w",
    yearly: process.env.NEXT_PUBLIC_STRIPE_BUSINESS_CAD_YEARLY_URL || "https://buy.stripe.com/28EfZj62ban4dN2bNXew80x",
  },
  enterprise: {
    monthly: process.env.NEXT_PUBLIC_STRIPE_ENTERPRISE_CAD_URL || "https://buy.stripe.com/00w9AV2PZ3YGcIYg4dew80y",
    yearly: process.env.NEXT_PUBLIC_STRIPE_ENTERPRISE_CAD_YEARLY_URL || "https://buy.stripe.com/00waEZ0HReDk6kAbNXew80z",
  },
};

/**
 * USD (native) pricing + live hardcoded Stripe Payment Links. SMB tiers carry
 * the no-setup monthly link; Business/Enterprise monthly links stay hardcoded
 * in Pricing.tsx (unchanged) so they are intentionally absent here. Every tier
 * has a live yearly link.
 */
const USD_PRICING: CurrencyPricingMap = {
  starter: {
    price: 69,
    yearlyTotal: 749,
    yearlyPerMonth: 62,
    overage: 0.25,
    setupFee: 0,
    monthlyUrl: "https://buy.stripe.com/00w3cxbmv3YG6kA05few80G",
    yearlyUrl: "https://buy.stripe.com/5kQ3cx3U366O38of09ew80H",
  },
  standard: {
    price: 139,
    yearlyTotal: 1499,
    yearlyPerMonth: 125,
    overage: 0.24,
    setupFee: 0,
    monthlyUrl: "https://buy.stripe.com/6oU7sNairan438o9FPew80I",
    yearlyUrl: "https://buy.stripe.com/7sYbJ3bmv0MubEU7xHew80J",
  },
  "high-volume": {
    price: 199,
    yearlyTotal: 2099,
    yearlyPerMonth: 175,
    overage: 0.23,
    setupFee: 0,
    monthlyUrl: "https://buy.stripe.com/9B6bJ31LV2UCbEU05few80K",
    yearlyUrl: "https://buy.stripe.com/4gM4gB4Y7br85gw2dnew80L",
  },
  business: {
    price: 699,
    yearlyTotal: 7499,
    yearlyPerMonth: 625,
    overage: 0.44,
    setupFee: SETUP_FEE,
    // Monthly link stays hardcoded in Pricing.tsx (see interface note).
    yearlyUrl: "https://buy.stripe.com/4gM4gBeyH9j05gwdW5ew80N",
  },
  enterprise: {
    price: 999,
    yearlyTotal: 10699,
    yearlyPerMonth: 892,
    overage: 0.35,
    setupFee: SETUP_FEE,
    // Monthly link stays hardcoded in Pricing.tsx (see interface note).
    yearlyUrl: "https://buy.stripe.com/9B614pairgLs6kA19jew80P",
  },
};

/**
 * CAD (cross) pricing. The friendly annual totals were approved directly. The
 * checkout links come from env (CAD_URLS); when absent the CTA falls back to a
 * contact page (never a USD link under a CAD price).
 */
const CAD_PRICING: CurrencyPricingMap = {
  starter: {
    price: 99,
    yearlyTotal: 1049,
    yearlyPerMonth: 87,
    overage: 0.25,
    setupFee: 0,
    monthlyUrl: CAD_URLS.starter.monthly,
    yearlyUrl: CAD_URLS.starter.yearly,
  },
  standard: {
    price: 199,
    yearlyTotal: 2099,
    yearlyPerMonth: 175,
    overage: 0.24,
    setupFee: 0,
    monthlyUrl: CAD_URLS.standard.monthly,
    yearlyUrl: CAD_URLS.standard.yearly,
  },
  "high-volume": {
    price: 299,
    yearlyTotal: 3199,
    yearlyPerMonth: 267,
    overage: 0.23,
    setupFee: 0,
    monthlyUrl: CAD_URLS["high-volume"].monthly,
    yearlyUrl: CAD_URLS["high-volume"].yearly,
  },
  business: {
    price: 999,
    yearlyTotal: 10699,
    yearlyPerMonth: 892,
    overage: 0.44,
    setupFee: SETUP_FEE,
    monthlyUrl: CAD_URLS.business.monthly,
    yearlyUrl: CAD_URLS.business.yearly,
  },
  enterprise: {
    price: 1499,
    yearlyTotal: 16099,
    yearlyPerMonth: 1342,
    overage: 0.35,
    setupFee: SETUP_FEE,
    monthlyUrl: CAD_URLS.enterprise.monthly,
    yearlyUrl: CAD_URLS.enterprise.yearly,
  },
};

/** Two-currency pricing map: currency -> plan -> pricing block. */
export const CURRENCY_PRICING: Record<Currency, CurrencyPricingMap> = {
  USD: USD_PRICING,
  CAD: CAD_PRICING,
};

/** Convenience: the pricing block for one plan in one currency. */
export function getPlanPricing(
  currency: Currency,
  key: DisplayPlanKey
): PlanCurrencyPricing {
  return CURRENCY_PRICING[currency][key];
}

/** Where a CTA points when the chosen currency/interval has no live link. */
export function contactHref(currency: Currency): string {
  return `/contact?subject=${encodeURIComponent(`${currency} Billing`)}`;
}

/**
 * Resolve a checkout URL for a plan/interval/currency. Returns the live link
 * when present, otherwise null so callers render the graceful contact
 * fallback. A `monthlyOverride` (used for the hardcoded Business/Enterprise
 * USD monthly links that live in Pricing.tsx) is honoured only when it is a
 * real http link (never used under a foreign currency).
 */
export function resolveCheckoutUrl(
  currency: Currency,
  key: DisplayPlanKey,
  interval: BillingInterval,
  monthlyOverride?: string
): string | null {
  const plan = CURRENCY_PRICING[currency][key];
  if (interval === "yearly") {
    return plan.yearlyUrl ?? null;
  }
  if (plan.monthlyUrl) return plan.monthlyUrl;
  // The hardcoded monthly override applies to the native currency only.
  if (currency === DEFAULT_CURRENCY && monthlyOverride?.startsWith("http")) {
    return monthlyOverride;
  }
  return null;
}

/** Label for the "contact us instead" CTA in a given currency. */
export function contactLabel(currency: Currency): string {
  return `Contact us for ${currency} billing`;
}

/** Format a whole-dollar amount in the given currency, e.g. 999 -> "$999". */
export function formatMoney(amount: number, currency: Currency): string {
  const locale = currency === "CAD" ? "en-CA" : "en-US";
  return `$${amount.toLocaleString(locale)}`;
}

/** Format a per-minute overage rate, e.g. 0.25 -> "$0.25". */
export function formatRate(rate: number): string {
  return `$${rate.toFixed(2)}`;
}
